import { useState, useRef, useEffect } from 'react';

function SignedImage({ path, className }: { path: string; className?: string }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    supabase.storage.from('session-photos').createSignedUrl(path, 3600)
      .then(({ data }) => { if (data?.signedUrl) setUrl(data.signedUrl); });
  }, [path]);
  if (!url) return <div className={className + ' bg-muted animate-pulse'} />;
  return <img src={url} alt="" className={className} />;
}
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useActiveSession, useSessions, useStartSession, useEndSession, useUpdateSession,
  useSessionMaterials, useAddSessionMaterial, useRemoveSessionMaterial,
} from '@/features/sessions/hooks/useSessions';
import { useProducts, Product, NEEDLE_CATALOG } from '@/features/inventory/hooks/useProducts';
import { useSignedContracts } from '@/shared/hooks/useCloudData';
import { useClients } from '@/features/clients/hooks/useClients';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { Play, Square, Camera, Package, FileText, Plus, Image, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const Sessions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('client_id');
  const { user } = useAuth();
  const { clients } = useClients();
  const linkedClient = clientIdParam ? clients.find(c => c.id === clientIdParam) : null;

  const activeSession = useActiveSession();
  const { data: sessions = [] } = useSessions();
  const startSession = useStartSession();
  const endSession = useEndSession();
  const updateSession = useUpdateSession();
  const { data: products = [] } = useProducts();
  const { data: materials = [] } = useSessionMaterials(activeSession?.id || null);
  const addMaterial = useAddSessionMaterial();
  const removeMaterial = useRemoveSessionMaterial();
  const { data: contracts = [] } = useSignedContracts();

  const [startDialogOpen, setStartDialogOpen] = useState(!!clientIdParam);
  const [newSession, setNewSession] = useState({
    client_name: linkedClient?.name || '',
    client_id: clientIdParam || '',
    project_name: '',
    contract_id: '',
  });
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialCategory, setMaterialCategory] = useState('all');
  const [selectedNeedleBrand, setSelectedNeedleBrand] = useState('');
  const [selectedNeedleRef, setSelectedNeedleRef] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleStart = () => {
    if (!newSession.client_name) { toast.error('Nom du client obligatoire'); return; }
    startSession.mutate({
      client_name: newSession.client_name,
      client_id: newSession.client_id || undefined,
      project_name: newSession.project_name,
      contract_id: newSession.contract_id || undefined,
    }, {
      onSuccess: (session) => {
        setStartDialogOpen(false);
        toast.success('Session démarrée');
        if (session?.id) navigate(`/sessions/${session.id}`);
      },
    });
  };

  const handleEnd = () => {
    if (!activeSession) return;
    if (confirm('Terminer cette session ?')) {
      endSession.mutate({ id: activeSession.id, elapsed_seconds: activeSession.elapsed_seconds || 0 }, {
        onSuccess: () => toast.success('Session terminée'),
      });
    }
  };

  const handleAddMaterial = (product: Product) => {
    if (!activeSession) return;
    addMaterial.mutate({ session_id: activeSession.id, product_id: product.id, quantity: 1 }, {
      onSuccess: () => toast.success(`${product.name} ajouté`),
    });
  };

  const handleAddNeedleByRef = () => {
    if (!activeSession || !selectedNeedleBrand || !selectedNeedleRef) return;
    // Find product matching brand + reference
    const match = products.find(
      p => p.category === 'needles' &&
        p.brand.toUpperCase() === selectedNeedleBrand.toUpperCase() &&
        p.reference.toUpperCase() === selectedNeedleRef.toUpperCase()
    );
    if (match) {
      handleAddMaterial(match);
    } else {
      toast.error('Produit non trouvé dans l\'inventaire. Ajoutez-le d\'abord dans Inventaire.');
    }
    setSelectedNeedleRef('');
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeSession || !user || !e.target.files?.length) return;
    const file = e.target.files[0];
    const path = `${user.id}/${activeSession.id}/${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from('session-photos').upload(path, file);
    if (error) { toast.error('Erreur upload'); return; }
    // Store the storage path (not a public URL) — signed URLs are generated at display time
    const newPhotos = [...(activeSession.photos || []), path];
    updateSession.mutate({ id: activeSession.id, photos: newPhotos } as any, {
      onSuccess: () => toast.success('Photo ajoutée'),
    });
  };

  const filteredProducts = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(materialSearch.toLowerCase()) || p.brand.toLowerCase().includes(materialSearch.toLowerCase());
    const matchCat = materialCategory === 'all' || p.category === materialCategory;
    return matchSearch && matchCat;
  });

  // Compute materials with product info
  const materialsList = materials.map(m => ({
    ...m,
    product: products.find(p => p.id === m.product_id),
  }));

  const pastSessions = sessions.filter(s => s.status === 'completed' || (s as any).status_v2 === 'completed');

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-display text-foreground flex items-center gap-3">
            <Play className="text-primary" size={28} />
            Historique des sessions
          </h1>
          <p className="text-muted-foreground mt-1">Sessions terminées — les sessions en cours sont visibles dans le bandeau</p>
        </div>
        {!activeSession && (
          <Button onClick={() => setStartDialogOpen(true)} className="gap-2">
            <Play size={16} /> Démarrer une session
          </Button>
        )}
      </motion.div>

      {/* Active session */}
      {activeSession && (
        <div className="space-y-4">
          {/* Session header */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="glass-card p-5 border-l-4 border-l-primary"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
                <h2 className="text-xl font-bold text-foreground">Session • {activeSession.client_name}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-primary text-primary">Session en cours</Badge>
                <Button variant="destructive" size="sm" onClick={handleEnd} className="gap-2">
                  <Square size={14} /> Terminer
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>Projet: {activeSession.project_name || '—'}</span>
              <span>Démarré: {format(parseISO(activeSession.started_at), "HH:mm", { locale: fr })}</span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Photos + Contract */}
            <div className="space-y-4">
              {/* Contract link */}
              <div className="glass-card p-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2 mb-3">
                  <FileText size={18} className="text-primary" /> Fiche de consentement
                </h3>
                {activeSession.contract_id ? (
                  <p className="text-sm text-muted-foreground">Contrat lié ✓</p>
                ) : (
                  <Select onValueChange={v => updateSession.mutate({ id: activeSession.id, contract_id: v } as any)}>
                    <SelectTrigger><SelectValue placeholder="Lier un consentement..." /></SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const filtered = (contracts as any[]).filter(c => {
                          if ((activeSession as any).client_id) return c.clientId === (activeSession as any).client_id;
                          return (c.clientName || c.client_name || '').toLowerCase().includes((activeSession.client_name || '').toLowerCase());
                        });
                        if (filtered.length === 0) return <SelectItem value="__none__" disabled>Aucun contrat trouvé pour ce client</SelectItem>;
                        return filtered.map((c: any) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.templateName || c.template_name} · {new Date(c.signedAt || c.signed_at).toLocaleDateString('fr-FR')}
                          </SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Photos */}
              <div className="glass-card p-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2 mb-3">
                  <Camera size={18} className="text-primary" /> Photos du projet
                </h3>
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
                <Button onClick={() => fileInputRef.current?.click()} className="w-full gap-2 mb-3" variant="outline">
                  <Camera size={16} /> Ajouter des photos
                </Button>
                {activeSession.photos && activeSession.photos.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {activeSession.photos.map((path, i) => (
                      <SignedImage key={i} path={path} className="rounded-lg object-cover aspect-square w-full" />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucune photo ajoutée</p>
                )}
              </div>
            </div>

            {/* Right: Materials */}
            <div className="glass-card p-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2 mb-3">
                <Package size={18} className="text-primary" /> Sélectionner le matériel
              </h3>
              <p className="text-sm text-muted-foreground mb-3">Ajoute le matériel utilisé pendant la session.</p>

              {/* Needle brand/reference quick-add */}
              <div className="p-3 rounded-xl bg-secondary/30 mb-3 space-y-2">
                <p className="text-xs font-semibold text-foreground">⚡ Ajout rapide — Aiguilles</p>
                <Select value={selectedNeedleBrand} onValueChange={v => { setSelectedNeedleBrand(v); setSelectedNeedleRef(''); }}>
                  <SelectTrigger><SelectValue placeholder="Choisir la marque..." /></SelectTrigger>
                  <SelectContent>
                    {NEEDLE_CATALOG.map(nc => (
                      <SelectItem key={nc.brand} value={nc.brand}>{nc.brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedNeedleBrand && (
                  <Select value={selectedNeedleRef} onValueChange={v => { setSelectedNeedleRef(v); }}>
                    <SelectTrigger><SelectValue placeholder="Choisir la référence..." /></SelectTrigger>
                    <SelectContent>
                      {NEEDLE_CATALOG.find(nc => nc.brand === selectedNeedleBrand)?.references.map(ref => (
                        <SelectItem key={ref} value={ref}>{ref}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedNeedleBrand && selectedNeedleRef && (
                  <Button size="sm" className="w-full gap-2" onClick={handleAddNeedleByRef}>
                    <Plus size={14} /> Ajouter {selectedNeedleBrand} {selectedNeedleRef}
                  </Button>
                )}
              </div>

              <Input
                value={materialSearch}
                onChange={e => setMaterialSearch(e.target.value)}
                placeholder="Rechercher un produit..."
                className="mb-3"
              />

              <div className="flex gap-2 flex-wrap mb-3">
                {['all', 'needles', 'inks', 'consumables', 'equipment', 'protection', 'other'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setMaterialCategory(cat)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      materialCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground'
                    }`}
                  >
                    {cat === 'all' ? 'Tous' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                {filteredProducts.map(product => (
                  <div key={product.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/30">
                    <div>
                      <p className="text-sm font-medium text-foreground">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.brand} • Stock: {product.stock}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleAddMaterial(product)} disabled={product.stock <= 0}>
                      <Plus size={14} />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Added materials */}
              {materialsList.length > 0 && (
                <div className="border-t border-border pt-3">
                  <h4 className="text-sm font-semibold text-foreground mb-2">Matériel ajouté</h4>
                  <div className="flex flex-wrap gap-2">
                    {materialsList.map(m => (
                      <Badge key={m.id} variant="secondary" className="gap-1 px-3 py-1">
                        {m.product?.name || 'Produit'} ×{m.quantity}
                        <button onClick={() => removeMaterial.mutate(m.id)}>
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Past sessions */}
      {!activeSession && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Sessions passées</h2>
          {pastSessions.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Clock size={40} className="mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">Aucune session passée</p>
            </div>
          ) : (
            pastSessions.map(s => (
              <div key={s.id} className="glass-card p-4 flex items-center justify-between cursor-pointer hover:border-primary/20 transition-colors"
                onClick={() => navigate(`/sessions/${s.id}`)}>
                <div>
                  <p className="font-semibold text-foreground">{s.client_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(parseISO(s.started_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                    {s.ended_at && ` — ${format(parseISO(s.ended_at), 'HH:mm', { locale: fr })}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {s.photos && s.photos.length > 0 && (
                    <Badge variant="secondary"><Image size={12} className="mr-1" /> {s.photos.length}</Badge>
                  )}
                  <Badge variant="outline">Terminée</Badge>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Start dialog */}
      <Dialog open={startDialogOpen} onOpenChange={setStartDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Démarrer une session</DialogTitle>
            <DialogDescription className="sr-only">Démarrer une nouvelle session pour un client</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Input value={newSession.client_name} onChange={e => setNewSession(p => ({ ...p, client_name: e.target.value }))} placeholder="Nom du client" disabled={!!linkedClient} />
              {linkedClient && <p className="text-xs text-primary mt-1">✓ Lié à la fiche client</p>}
            </div>
            <div>
              <Label>Projet</Label>
              <Input value={newSession.project_name} onChange={e => setNewSession(p => ({ ...p, project_name: e.target.value }))} placeholder="Ex: Manchette dragon" />
            </div>
            <div>
              <Label>Fiche de consentement</Label>
              <Select value={newSession.contract_id} onValueChange={v => setNewSession(p => ({ ...p, contract_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Lier un consentement (optionnel)" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Filter by clientId first, fallback to name match
                    const clientId = newSession.client_id;
                    const clientName = newSession.client_name.toLowerCase().trim();
                    const filtered = (contracts as any[]).filter(c => {
                      if (clientId) return c.clientId === clientId;
                      if (clientName) return (c.clientName || c.client_name || '').toLowerCase().includes(clientName);
                      return true;
                    });
                    if (filtered.length === 0) return (
                      <SelectItem value="__none__" disabled>Aucun contrat trouvé pour ce client</SelectItem>
                    );
                    return filtered.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.templateName || c.template_name}
                        {' · '}
                        <span className="text-muted-foreground text-xs">{c.clientName || c.client_name}</span>
                        {' · '}
                        <span className="text-muted-foreground text-xs">{new Date(c.signedAt || c.signed_at).toLocaleDateString('fr-FR')}</span>
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleStart} disabled={startSession.isPending}>Démarrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sessions;
